import {
  type AIProviderId,
  type EffortLevel,
  getAIProvider,
  isEffortLevel,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import { emptyCredentials } from "../../../shared/aiProviders/manifest";
import type { AiProvider as AiConfig } from "../../../shared/generatedDrizzleSchema";

export type AIConfigDraft = {
  provider: AIProviderId;
  credentials: Record<string, string>;
  chatModel: string;
  fastModel: string;
  effort: EffortLevel | null;
  connectedAppId: string | null;
  inheritConnectorAccess: boolean;
};

export function emptyDraft(
  provider: AIProviderId = "anthropic"
): AIConfigDraft {
  return {
    provider,
    credentials: emptyCredentials(getAIProvider(provider).credentialFields),
    chatModel: "",
    fastModel: "",
    effort: null,
    connectedAppId: null,
    inheritConnectorAccess: false,
  };
}

export function toDraft(
  config: AiConfig,
  connectedAppId: string | null = null
): AIConfigDraft {
  const provider = resolveAIProviderId(config);
  const definition = getAIProvider(provider);
  return {
    provider,
    credentials: definition.unpackCredentials({
      baseUrl: config.baseUrl ?? "",
      apiKey: "",
    }),
    chatModel: config.chatModel ?? "",
    fastModel: config.fastModel ?? "",
    effort: isEffortLevel(config.effort) ? config.effort : null,
    connectedAppId,
    inheritConnectorAccess: config.inheritConnectorAccess ?? false,
  };
}

// The env-managed provider has no ai_providers row; its chat model and effort
// are the tenant admin's stored defaults (blank/null = the hardcoded managed
// default). The fast model is derived from the chat model's family and shown
// read-only, so it isn't part of the saved draft.
export function managedDraft(
  provider: AIProviderId,
  stored?: { chatModel?: string | null; effort?: EffortLevel | null }
): AIConfigDraft {
  const definition = getAIProvider(provider);
  return {
    provider,
    credentials: emptyCredentials(definition.credentialFields),
    chatModel: stored?.chatModel ?? "",
    fastModel: "",
    effort: stored?.effort ?? null,
    connectedAppId: null,
    inheritConnectorAccess: false,
  };
}

export function toMutationArgs(
  id: string,
  active: boolean,
  draft: AIConfigDraft
) {
  const definition = getAIProvider(draft.provider);
  const packed = definition.packCredentials(draft.credentials);
  // Every read derives a connector-backed endpoint from the connector, so a copy
  // stored here could only go stale.
  const baseUrl = definition.endpointFromConnector ? "" : packed.baseUrl;
  return {
    id,
    active,
    provider: draft.provider,
    baseUrl,
    chatModel: draft.chatModel.trim(),
    fastModel: draft.fastModel.trim(),
    effort: draft.effort,
    connectedAppId: draft.connectedAppId,
    inheritConnectorAccess: draft.inheritConnectorAccess,
  };
}

export function saveErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  return `AI provider couldn’t be saved: ${message}`;
}

export function draftSaveDisabled(
  draft: AIConfigDraft,
  reusePrior: boolean
): boolean {
  const draftProvider = getAIProvider(draft.provider);
  const usesConnector = draft.connectedAppId != null;
  const missingRequired = draftProvider.credentialFields.some((field) => {
    if (!field.required) return false;
    if (field.sensitive && (reusePrior || usesConnector)) return false;
    return !draft.credentials[field.key]?.trim();
  });
  // A blank chat model persists as null and silently resolves to the manifest's
  // example id, so a provider that requires one has to name it here.
  return (
    missingRequired ||
    (draft.inheritConnectorAccess && !draft.connectedAppId) ||
    (draftProvider.requiresChatModel && !draft.chatModel.trim())
  );
}
